import { ChevronDown, FileText, Search } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { OFFER_DRAFT_STAMP, type OfferLetterPreview, type OfferPreviewChoice } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Input } from '../../../../components/Input';
import { MenuItem, Popover, usePopover } from '../../../../components/Popover';
import { SkeletonLines } from '../../../../components/Skeleton';
import { errorMessage } from '../../../../lib/api-client';
import { useFocusOnOpen } from '../../../../lib/use-focus-on-open';
import { useSettledValue } from '../../../../lib/use-settled-value';
import { useToasts } from '../../../../stores/toasts';
import { openOfferLetterPreviewPdf, useOfferLetterPreview, useOfferPreviewChoices } from '../../api/offers-api';
import { previewRequestOf, type WatchedLetter } from './offer-letter-form';
import styles from './LetterPreview.module.css';

/** The page redraws once typing pauses this long. */
const PREVIEW_SETTLE_MS = 400;
const CHOOSER_WIDTH = 320;
/** Past this many offers the chooser gets a search box. */
const CHOOSER_SEARCH_FROM = 6;

/** Which values fill the page: an offer the Admin picked, sample values, or (until they pick) this letter's newest offer. */
export type PreviewSource = { readonly kind: 'auto' } | { readonly kind: 'sample' } | { readonly kind: 'offer'; readonly offerId: string };

/**
 * The A4 page (Option A): the letter exactly as the PDF prints it — the company, the reference line, the paragraphs,
 * DRAFT across the page — filled from a real offer or from sample values, with each filled value marked. No letterhead,
 * address block or signature the PDF doesn't print. A field this viewer may not see prints "—" (#5).
 */
export function LetterPreview({ values, letterId }: { readonly values: WatchedLetter; readonly letterId: string | null }): ReactNode {
  const [source, setSource] = useState<PreviewSource>({ kind: 'auto' });
  const choices = useOfferPreviewChoices(letterId, true);
  const offerId = offerIdOf(source, choices.data);
  const request = previewRequestOf(values, offerId);
  // Typing waits for a pause; picking another offer redraws at once. Until the offers are known, "auto" has nothing
  // to pick from, so the page waits rather than flash sample values first.
  const settledLetter = useSettledValue(request.letter, PREVIEW_SETTLE_MS);
  const isWaitingForChoices = source.kind === 'auto' && choices.isPending;
  const preview = useOfferLetterPreview(isWaitingForChoices ? null : { letter: settledLetter, ...(offerId ? { offerId } : {}) });
  const show = useToasts((state) => state.show);
  const [isOpeningPdf, setIsOpeningPdf] = useState(false);

  async function openPdf(): Promise<void> {
    setIsOpeningPdf(true);
    try {
      await openOfferLetterPreviewPdf(request);
    } catch (cause) {
      show({ tone: 'danger', message: "Couldn't open the PDF", detail: errorMessage(cause) });
    } finally {
      setIsOpeningPdf(false);
    }
  }

  return (
    <section className={styles.pane} aria-label="A4 preview of the letter">
      <div className={styles.bar}>
        <span className={styles.label}>
          <FileText size={16} aria-hidden />
          A4 preview
        </span>
        <SourceChooser choices={choices.data ?? []} offerId={offerId} onChoose={setSource} />
        <Button size="sm" variant="quiet" isPending={isOpeningPdf} onClick={() => void openPdf()}>
          Open as PDF
        </Button>
      </div>
      <PreviewNotes preview={preview.data} isCatchingUp={preview.isFetching || JSON.stringify(settledLetter) !== JSON.stringify(request.letter)} />
      <PreviewBody
        preview={preview.data}
        error={preview.error}
        onRetry={() => void preview.refetch()}
        onUseSamples={() => setSource({ kind: 'sample' })}
        isFromOffer={offerId !== null}
      />
    </section>
  );
}

/** Until the Admin picks, the newest offer that prints this letter fills the page; with none, sample values. */
function offerIdOf(source: PreviewSource, choices: readonly OfferPreviewChoice[] | undefined): string | null {
  if (source.kind === 'offer') return source.offerId;
  if (source.kind === 'sample') return null;
  return choices?.find((choice) => choice.usesThisLetter)?.id ?? null;
}

function chooserLabel(chosen: OfferPreviewChoice | undefined, offerId: string | null): string {
  if (chosen) return `With ${chosen.candidateName}'s offer`;
  return offerId ? 'With the chosen offer' : 'With sample values';
}

function SourceChooser({
  choices,
  offerId,
  onChoose,
}: {
  readonly choices: readonly OfferPreviewChoice[];
  readonly offerId: string | null;
  readonly onChoose: (source: PreviewSource) => void;
}): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [query, setQuery] = useState('');
  const search = useRef<HTMLInputElement | null>(null);
  const hasSearch = choices.length > CHOOSER_SEARCH_FROM;
  useFocusOnOpen(search, isOpen && hasSearch);
  const chosen = choices.find((choice) => choice.id === offerId);
  const wanted = query.trim().toLowerCase();
  const shown = wanted === '' ? choices : choices.filter((choice) => [choice.candidateName, choice.reference, choice.jobTitle].some((text) => text.toLowerCase().includes(wanted)));
  const pick = (next: PreviewSource): void => {
    close();
    onChoose(next);
  };

  return (
    <>
      <Button
        ref={triggerRef}
        size="sm"
        variant="outline"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={() => {
          setQuery('');
          toggle();
        }}
      >
        {chooserLabel(chosen, offerId)}
        <ChevronDown size={14} aria-hidden />
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" width={CHOOSER_WIDTH} label="Preview with">
        <div className={styles.chooser}>
          {hasSearch && <Input ref={search} icon={Search} value={query} placeholder="Find an offer" aria-label="Find an offer" onChange={(event) => setQuery(event.target.value)} />}
          <MenuItem isSelected={offerId === null} meta="Not a real offer" onClick={() => pick({ kind: 'sample' })}>
            Sample values
          </MenuItem>
          {shown.map((choice) => (
            <MenuItem
              key={choice.id}
              isSelected={choice.id === offerId}
              meta={`${choice.reference} · ${choice.jobTitle}${choice.usesThisLetter ? ' · uses this letter' : ''}`}
              onClick={() => pick({ kind: 'offer', offerId: choice.id })}
            >
              {choice.candidateName}
            </MenuItem>
          ))}
          {choices.length === 0 && <p className={styles.chooserNote}>No offers you can open yet — sample values fill the page.</p>}
        </div>
      </Popover>
    </>
  );
}

function PreviewNotes({ preview, isCatchingUp }: { readonly preview: OfferLetterPreview | undefined; readonly isCatchingUp: boolean }): ReactNode {
  if (!preview) return null;
  return (
    <div className={styles.notes} aria-live="polite">
      {preview.source === 'sample' ? (
        <p className={styles.note}>
          <strong className={styles.strong}>Sample values</strong> — not a real offer. The marked words show where each field lands.
        </p>
      ) : (
        <p className={styles.note}>
          Filled from <strong className={styles.strong}>{preview.offer?.reference ?? '—'}</strong>, {preview.offer?.candidateName ?? '—'}. Nothing is saved or sent.
        </p>
      )}
      {preview.restrictedFields.length > 0 && <p className={styles.note}>Some fields are hidden from you, so they print as —.</p>}
      {isCatchingUp && <p className={styles.note}>Updating…</p>}
    </div>
  );
}

function PreviewBody({
  preview,
  error,
  onRetry,
  onUseSamples,
  isFromOffer,
}: {
  readonly preview: OfferLetterPreview | undefined;
  readonly error: Error | null;
  readonly onRetry: () => void;
  readonly onUseSamples: () => void;
  readonly isFromOffer: boolean;
}): ReactNode {
  if (error) {
    return (
      <div className={styles.problem} role="alert">
        <p className={styles.problemText}>Couldn't draw the page. {errorMessage(error)}</p>
        <span className={styles.problemActions}>
          <Button size="sm" variant="outline" onClick={onRetry}>
            Try again
          </Button>
          {isFromOffer && (
            <Button size="sm" variant="quiet" onClick={onUseSamples}>
              Use sample values
            </Button>
          )}
        </span>
      </div>
    );
  }
  if (!preview) {
    return (
      <div className={styles.sheetFrame}>
        <div className={styles.sheet}>
          <SkeletonLines count={10} />
        </div>
      </div>
    );
  }
  return <A4Page preview={preview} />;
}

/** The PDF's own layout, scaled to the pane: A4's proportions, its margins, its three text sizes. */
function A4Page({ preview }: { readonly preview: OfferLetterPreview }): ReactNode {
  return (
    <div className={styles.sheetFrame}>
      <article className={styles.sheet} aria-label="The letter, as printed">
        <span className={styles.stamp} aria-hidden>
          {OFFER_DRAFT_STAMP}
        </span>
        <p className={styles.company}>{preview.companyName}</p>
        <p className={styles.reference}>{preview.referenceLine}</p>
        {preview.paragraphs.length === 0 && <p className={styles.empty}>The letter is empty. Write it on the left.</p>}
        {preview.paragraphs.map((paragraph, index) => (
          <p key={index} className={styles.paragraph}>
            {paragraph.map((segment, at) => (
              <Segment key={at} text={segment.text} field={segment.field} isUnknownField={segment.isUnknownField} isRestricted={segment.field !== null && preview.restrictedFields.includes(segment.field)} />
            ))}
          </p>
        ))}
      </article>
    </div>
  );
}

function Segment({ text, field, isUnknownField, isRestricted }: { readonly text: string; readonly field: string | null; readonly isUnknownField: boolean; readonly isRestricted: boolean }): ReactNode {
  if (isUnknownField) return <span className={styles.unknown} title="Not a letter field: it prints as typed">{text}</span>;
  if (field === null) return text;
  if (isRestricted) return <span className={styles.restricted} title="Hidden from you">{text}</span>;
  return <mark className={styles.filled}>{text}</mark>;
}
